/* eslint-disable no-unused-expressions */
import React, { useState, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { Container } from '@material-ui/core';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import {
  setEmailLogin,
  loginSuccess,
  deleteLoginData,
  setTermsExpirationDate,
  setTermsVersion,
  setIsTermsExpired,
  setTermsConditions,
} from '@store/login/actions';
import { showError, hideSnackBar } from '@store/common/actions';
import Subtitle from '@components/Forms/Subtitle';
import InputText from '@components/Inputs/InputText';
import { INITIAL_FORM, DATAMART_OB, SELLER_HOME, TRANSACTIONS } from '@constants/routes';
import { REGIONS } from '@constants/regions';
import LoginHeader from '@components/LoginHeader';
import Boton from '@components/Forms/Boton';
import GenericForm from '@components/Forms/GenericForm';
import { login, tyc } from '@services/login-service';
import { getPermissions } from '@services/util-service';
import Title from '@components/Forms/Title';
import PasswordText from '@components/Inputs/PasswordText';
import ModalTermsAndConditions from '@components/Modal/ModalTermsAndConditions';
import { MetrixApi, BlockedAccountModal, LoginRecovery } from '@components';
import useAuth, { sellerTypes } from '@hooks/useAuth';
import { generateRandomToLength } from '@helpers/utils';
import { getEnv } from '@config';
import { formFieldsHaveValue } from '@helpers/validator';

import schema from './schema';
import { Spinner } from '@components/Spinner/SpinnerWithLessStyle';

const { country } = getEnv();

const ERROR_TERMS_AND_CONDITIONS_LEGAL_AGENT = 'TYC-00';
const ERROR_TERMS_AND_CONDITIONS_MERCHANT = 'TYC-01';
const ERROR_MESSAGE_USER_DISABLED = 'User disabled';

const LoginForm = () => {
  const storeLogin = useSelector((state) => state.login);
  const { isAllowed } = useAuth();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const buttonStyle = {
    margin: '20px auto',
    display: 'table',
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const [email, setEmail] = useState(storeLogin.saved_info.email || '');
  const [error, setError] = useState(false);
  const [password, setPassword] = useState(storeLogin.saved_info.password || '');
  const [showModal, setShowModal] = useState(false);
  const [showModalTerms, setShowModalTerms] = useState(false);
  const [userProfileId, setUserProfileId] = useState(null);
  const [isLegalAgent, setIsLegalAgent] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [loadPermissions, setLoadPermissions] = useState(true);

  const handleToggleModal = () => {
    setShowModal((prevShowModal) => !prevShowModal);
  };

  const hasPermission = (permission) => permissions.indexOf(permission) !== -1;

  const fetchPermissions = async () => {
    setLoadPermissions(true);
    try {
      const { data } = await getPermissions();
      setPermissions(data);
      // eslint-disable-next-line no-shadow
    } catch (error) {
      setPermissions([]);
    }
    setLoadPermissions(false);
  };

  useEffect(() => {
    fetchPermissions();
    setSessionId(generateRandomToLength(64).toLowerCase());

    dispatch(hideSnackBar());
    if (storeLogin.is_expired) {
      const expiredMessage = {
        message: t('login.login.expiredMessage'),
      };
      expiredMessage.snackBarType = 'info';
      dispatch(showError(expiredMessage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const historyPushHome = () => {
    if (isAllowed('HOME')) {
      history.push(SELLER_HOME);
      return;
    }

    if (isAllowed('TRANSACTIONS')) {
      history.push(TRANSACTIONS);
    }

    // FIXME: Se deja como mejora crear una screen "not-allowed" o similar para tener una url
    // donde redireccionar al usuario que sea independiente los permisos RBAC.
  };

  const handleLoginSucces = async (response) => {
    dispatch(deleteLoginData());
    dispatch(setEmailLogin({ ...response.merchant }));

    if (response.jwt) {
      const isOnus = response.merchant.seller === sellerTypes.ON_US;
      dispatch(loginSuccess({ ...response, isOnus }));
      if (response?.tyc_warning && response?.loguedAs?.is_legal_agent) {
        dispatch(setTermsExpirationDate(response.tyc_warning?.limit));
        dispatch(setTermsVersion(response.tyc_warning?.last_version));
        dispatch(setIsTermsExpired(false));
        setUserProfileId(response.loguedAs?._id);
        setIsLegalAgent(true);
        setShowModalTerms(true);
      } else {
        if (response?.tyc_warning) {
          dispatch(setTermsConditions(response.tyc_warning));
        }
        historyPushHome();
      }
    }
    setLoadingLogin(false);
  };

  const handleCancelTos = () => {
    if (storeLogin?.is_terms_expired) {
      setShowModalTerms(false);
    } else {
      historyPushHome();
    }
  };

  const handleLoginError = (errorLogin) => {
    setLoadingLogin(false);
    if (errorLogin?.code === ERROR_TERMS_AND_CONDITIONS_LEGAL_AGENT) {
      setUserProfileId(errorLogin?.id);
      dispatch(setTermsVersion(errorLogin?.last_version));
      dispatch(setIsTermsExpired(true));
      setShowModalTerms(true);
      setIsLegalAgent(true);
      return;
    }

    if (errorLogin?.code === ERROR_TERMS_AND_CONDITIONS_MERCHANT) {
      dispatch(setIsTermsExpired(true));
      setShowModalTerms(true);
      setIsLegalAgent(false);
      return;
    }

    if (errorLogin?.message === ERROR_MESSAGE_USER_DISABLED) {
      handleToggleModal();
      return;
    }

    const updatedError = {
      ...errorLogin,
      message:t('login.errors.incorrectCredentials'),
    };

    setError(true);
    dispatch(showError(updatedError));
  };

  const handleSubmit = () => {
    if (email && password && !loadingLogin) {
      setLoadingLogin(true);
      login({ email, password, sessionId }, handleLoginSucces, handleLoginError);
    }
  };

  const handleTycSuccess = () => {
    if (storeLogin?.is_terms_expired) {
      handleSubmit();
    } else {
      historyPushHome();
    }
  };

  const handleTycError = (res) => {
    setShowModalTerms(false);
    dispatch(showError(res));
  };

  const handleAcceptTos = () => {
    if (isLegalAgent) {
      tyc(
        { merchantId: userProfileId, version: storeLogin?.terms_version },
        handleTycSuccess,
        handleTycError
      );
    } else {
      setShowModalTerms(false);
    }
  };

  const handleOnKeydown = (event) => {
    if (event.keyCode === 13) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const isSubmitDisabled = error || formFieldsHaveValue([email, password]);

  return (
    <>
      <Spinner isLoading={loadingLogin} aligned fixed />;
      <Container className={`formContainer ${isMobile ? 'mobile' : ''}`}>
        <LoginHeader />
        <GenericForm
          schema={schema}
          item
          container
          spacing={2}
          onSubmit={handleSubmit}
          className="form login-form"
          xs={12}
          sm={10}
          md={6}
          lg={4}
          justifyContent="center"
          style={{ textAlign: 'center' }}
        >
          <Title
            gridProps={{ xs: 12, item: true }}
            id="0"
            style={{ margin: '0px auto 0 auto' }}
            className="headerTitle"
            text={t('login.login.title')}
            testID="login-title"
          />
          <Subtitle
            style={{ textAlign: 'center', marginTop: 20 }}
            id="1"
            className="headerTitle"
            text={t('login.login.subTitleMerchant')}
            testID="login-subTitle"
          />
          <p
            className="subTitle"
            id="2"
            style={{
              fontWeight: 'normal',
              fontSize: 16,
              color: 'var(--blue_grey)',
              textAlign: 'center',
              marginTop: 20,
              display: !loadPermissions && REGIONS[country].onboarding ? 'block' : 'none',
            }}
          >
            {t('login.login.areNew')} &nbsp;
            <Link
              to={hasPermission('DATAMART_OB') ? DATAMART_OB : INITIAL_FORM}
              style={{ textDecoration: 'none', color: 'var(--grass)' }}
              id="onb-login-createAccount"
            >
              {t('login.login.newAccount')}
            </Link>
          </p>
          <InputText
            onKeyDown={(e) => handleOnKeydown(e)}
            value={email}
            label={t('common.email')}
            handleChange={(e) => {
              setEmail(e.target.value);
              setError(false);
            }}
            error={error}
            id="email"
            fullWidth
            variant="outlined"
            gridProps={{ xs: 12, item: true }}
          />
          <PasswordText
            onKeyDown={(e) => handleOnKeydown(e)}
            value={password}
            fullWidth
            error={error}
            variant="outlined"
            label={t('common.password')}
            gridProps={{ item: true, xs: 12, padding: '0' }}
            handleChange={(e) => {
              setError(false);
              setPassword(e.target.value);
            }}
            id="password"
            name="password"
          />
          {!loadPermissions ? (
            <LoginRecovery activeUserRecovery={hasPermission('USER_RECOVERY')} />
          ) : (
            /** Requiered by GenericForm.render. Error: TypeError: Cannot read properties of undefined (reading 'id') */
            <></>
          )}
          <Boton
            gridProps={{ item: true, xs: 12 }}
            type="submit"
            text={t('common.buttonLogin')}
            style={buttonStyle}
            disabled={isSubmitDisabled}
          />
        </GenericForm>
      </Container>
      {showModalTerms && (
        <ModalTermsAndConditions
          isLegalAgent={isLegalAgent}
          isExpired={storeLogin?.is_terms_expired}
          expirationDate={storeLogin?.terms_expiration_date}
          open
          extraWidth
          handleCancel={handleCancelTos}
          handleConfirm={handleAcceptTos}
        />
      )}
      {showModal && (
        <BlockedAccountModal
          fClose={handleToggleModal}
          title={t('login.resetPassword.titleModal')}
          subTitle={t('login.resetPassword.common.lockedPassword')}
          textClose={t(
            isMobile ? 'login.resetPassword.common.titleSmall' : 'login.resetPassword.common.title'
          )}
        />
      )}
      <MetrixApi sessionId={sessionId} />
    </>
  );
};

export default LoginForm;
